import { type ReactNode, createContext, useCallback, useContext } from 'react'

import { type HapticKind, hapticTap } from './hapticTap'
import { logger } from '../logging/logger'

export type { HapticKind } from './hapticTap'

/**
 * A short tactile tap confirming a discrete input, such as a keypad press.
 * Called with `'error'` for refused input (deleting from empty, typing past a
 * limit), which the default implementation answers with a heavier buzz.
 *
 * Taps are part of the design rather than a garnish, so the SDK provides them
 * itself from the optional `react-native-haptic-feedback` peer — see
 * {@link hapticTap}. Hosts on another module (`expo-haptics` in Expo Go), or
 * wanting silence, pass their own through `FunkitProvider` — one that ignores
 * the kind still works, it just answers everything with the same tap.
 */
export type HapticFeedback = (kind?: HapticKind) => void | Promise<void>

const HapticsContext = createContext<HapticFeedback>(hapticTap)

export function FunkitHapticsProvider({
  hapticFeedback = hapticTap,
  children,
}: {
  hapticFeedback?: HapticFeedback
  children: ReactNode
}) {
  return (
    <HapticsContext.Provider value={hapticFeedback}>
      {children}
    </HapticsContext.Provider>
  )
}

// A misconfigured host would otherwise warn on every single key. The tap is
// cosmetic, so saying it once is enough to debug it.
let hasWarned = false

/**
 * Fire-and-forget haptics. A host's native module must never take a keypress
 * down with it, so a throw or a rejection is swallowed — the key still registers
 * and the amount still changes.
 */
export function useHapticFeedback(): (kind?: HapticKind) => void {
  const haptic = useContext(HapticsContext)

  return useCallback(
    (kind?: HapticKind) => {
      const warn = (error: Error) => {
        if (hasWarned) {
          return
        }
        hasWarned = true
        logger.warn('connect-rn: haptic feedback failed; taps will be silent', {
          error: error.message,
        })
      }
      // A throw is any value; the log wants a message, so non-Errors are
      // wrapped at each catch boundary.
      try {
        Promise.resolve(haptic(kind)).catch((error) =>
          warn(error instanceof Error ? error : new Error(String(error))),
        )
      } catch (error) {
        warn(error instanceof Error ? error : new Error(String(error)))
      }
    },
    [haptic],
  )
}
